import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  MessageSquare, 
  CreditCard, 
  Mail, 
  Check,
  X,
  Loader2,
  RefreshCw,
  FileText,
  Key,
  Copy,
  Trash2,
  Plus,
  Globe,
  Eye,
  EyeOff,
  AlertTriangle,
  Code2,
  Zap,
  BookOpen,
  CheckCircle2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { getCurrencySymbol } from "@/lib/currency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const getDefaultWhatsappTemplates = () => ({
  reminder_start: "Hola {nombre}, tu alquiler de equipos de esquí comienza mañana {fecha_inicio}. ¡Te esperamos!",
  reminder_end: "Hola {nombre}, recuerda devolver tu equipo hoy antes de las 20:00h. Fecha fin: {fecha_fin}",
  overdue: "Hola {nombre}, tu equipo debía devolverse el {fecha_fin}. Por favor contacta con nosotros.",
  confirmation: `Reserva confirmada para {nombre}. {articulos} desde {fecha_inicio} hasta {fecha_fin}. Total: {total} ${getCurrencySymbol()}`
});

export default function Integrations() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [whatsappConfig, setWhatsappConfig] = useState({
    enabled: false,
    phone_number: "",
    api_key: "",
    auto_reminders: false,
    send_confirmations: false,
    send_overdue_alerts: false,
    templates: getDefaultWhatsappTemplates()
  });
  const [tpvConfig, setTpvConfig] = useState({
    enabled: false,
    bank: "",
    terminal_model: "",
    terminal_number: "",
    api_key: "",
    auto_sync: true,
    save_receipts: true,
    reprint_enabled: false
  });
  const [verifactuConfig, setVerifactuConfig] = useState({
    enabled: false,
    nif_emisor: "",
    nombre_emisor: "",
    software_id: "",
    software_version: "1.0",
    test_mode: true,
    auto_send: false,
    save_xml: true
  });
  const [emailConfig, setEmailConfig] = useState({
    enabled: false,
    smtp_server: "",
    smtp_port: "587",
    email_from: "",
    email_password: "",
    use_tls: true,
    send_confirmations: false,
    send_reminders: false,
    send_invoices: false,
    company_logo_url: "",
    email_signature: ""
  });

  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [creatingKey, setCreatingKey] = useState(false);
  const [newKeyRevealed, setNewKeyRevealed] = useState(null); // { key, name } - shown once
  const [showKeyValue, setShowKeyValue] = useState(false);

  useEffect(() => {
    loadConfigs();
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.get(`${API}/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setApiKeys(data);
    } catch {
      // silently fail
    }
  };

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) return toast.error("Escribe un nombre para la API Key");
    setCreatingKey(true);
    try {
      const token = localStorage.getItem("token");
      const { data } = await axios.post(`${API}/api-keys`, { name: newKeyName.trim() }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewKeyRevealed({ raw_key: data.raw_key, name: data.name });
      setShowKeyValue(false);
      setNewKeyName("");
      loadApiKeys();
      toast.success("API Key generada. ¡Cópiala ahora, no se mostrará otra vez!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al generar la API Key");
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeKey = async (keyId, keyName) => {
    if (!window.confirm(`¿Revocar la API Key "${keyName}"? Esta acción no se puede deshacer.`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/api-keys/${keyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("API Key revocada");
      loadApiKeys();
    } catch {
      toast.error("Error al revocar la API Key");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copiado al portapapeles"));
  };

  const loadConfigs = async () => {
    try {
      const [whatsappRes, tpvRes, verifactuRes, emailRes] = await Promise.all([
        axios.get(`${API}/integrations/config/whatsapp`),
        axios.get(`${API}/integrations/config/tpv`),
        axios.get(`${API}/integrations/config/verifactu`),
        axios.get(`${API}/integrations/config/email`)
      ]);
      
      if (whatsappRes.data.config) {
        setWhatsappConfig({ ...whatsappConfig, ...whatsappRes.data.config, enabled: whatsappRes.data.enabled });
      }
      if (tpvRes.data.config) {
        setTpvConfig({ ...tpvConfig, ...tpvRes.data.config, enabled: tpvRes.data.enabled });
      }
      if (verifactuRes.data.config) {
        setVerifactuConfig({ ...verifactuConfig, ...verifactuRes.data.config, enabled: verifactuRes.data.enabled });
      }
      if (emailRes.data.config) {
        setEmailConfig({ ...emailConfig, ...emailRes.data.config, enabled: emailRes.data.enabled });
      }
    } catch (error) {
      console.log("Configs not loaded yet");
    } finally {
      setLoading(false);
    }
  };

  const saveWhatsappConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/integrations/config`, {
        integration_type: "whatsapp",
        enabled: whatsappConfig.enabled,
        config: whatsappConfig
      });
      toast.success("Configuración de WhatsApp guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const saveTpvConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/integrations/config`, {
        integration_type: "tpv",
        enabled: tpvConfig.enabled,
        config: tpvConfig
      });
      toast.success("Configuración de TPV guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const saveVerifactuConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/integrations/config`, {
        integration_type: "verifactu",
        enabled: verifactuConfig.enabled,
        config: verifactuConfig
      });
      toast.success("Configuración de VeriFactu guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const saveEmailConfig = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/integrations/config`, {
        integration_type: "email",
        enabled: emailConfig.enabled,
        config: emailConfig
      });
      toast.success("Configuración de Email guardada");
    } catch (error) {
      toast.error("Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async (type) => {
    toast.info(`Probando conexión con ${type}...`);
    // Simulated test - in real implementation would call actual API
    setTimeout(() => {
      toast.success(`Conexión con ${type} simulada correctamente`);
    }, 1500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="integrations-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
          Integraciones
        </h1>
        <p className="text-slate-500 mt-1">Configura conexiones con servicios externos</p>
      </div>

      <Tabs defaultValue="api" className="space-y-6">
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
          <TabsTrigger value="api" className="gap-2">
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline">API / Web</span>
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="gap-2">
            <MessageSquare className="h-4 w-4" />
            <span className="hidden sm:inline">WhatsApp</span>
          </TabsTrigger>
          <TabsTrigger value="tpv" className="gap-2">
            <CreditCard className="h-4 w-4" />
            <span className="hidden sm:inline">TPV</span>
          </TabsTrigger>
          <TabsTrigger value="verifactu" className="gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">VeriFactu</span>
          </TabsTrigger>
          <TabsTrigger value="email" className="gap-2">
            <Mail className="h-4 w-4" />
            <span className="hidden sm:inline">Email</span>
          </TabsTrigger>
        </TabsList>

        {/* ── API Keys Tab ── */}
        <TabsContent value="api" className="space-y-6">
          {/* Hero intro card */}
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-6">
              <div className="flex gap-5 items-start">
                <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center flex-shrink-0 shadow-lg">
                  <Globe className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-xl text-slate-900 mb-2">API REST Universal</h3>
                  <p className="text-slate-600 leading-relaxed mb-4">
                    Recibe reservas desde <strong>cualquier plataforma web</strong>: Wix, Squarespace, Webflow, 
                    WordPress, código personalizado, o automatizaciones con <strong>Zapier</strong>, <strong>Make</strong>, <strong>n8n</strong>.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="bg-white">Wix</Badge>
                    <Badge variant="outline" className="bg-white">Squarespace</Badge>
                    <Badge variant="outline" className="bg-white">Webflow</Badge>
                    <Badge variant="outline" className="bg-white">WordPress</Badge>
                    <Badge variant="outline" className="bg-white">Shopify</Badge>
                    <Badge variant="outline" className="bg-white">Custom Code</Badge>
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      <Zap className="h-3 w-3 mr-1" />
                      Zapier
                    </Badge>
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      <Zap className="h-3 w-3 mr-1" />
                      Make
                    </Badge>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* New revealed key banner */}
          {newKeyRevealed && (
            <Card className="border-amber-300 bg-amber-50" data-testid="new-key-banner">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-amber-800">¡Copia tu API Key ahora!</p>
                    <p className="text-sm text-amber-700 mt-0.5">
                      Esta clave solo se muestra una vez. Guárdala en un lugar seguro.
                    </p>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <div className="flex-1 font-mono text-sm bg-white border border-amber-200 rounded-lg px-3 py-2.5 text-slate-700 truncate">
                    {showKeyValue ? newKeyRevealed.raw_key : "sk_live_••••••••••••••••••••••••••••••••"}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setShowKeyValue(!showKeyValue)}
                    title={showKeyValue ? "Ocultar" : "Mostrar"}
                  >
                    {showKeyValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(newKeyRevealed.raw_key)}
                    className="gap-2 shrink-0"
                    data-testid="copy-api-key-btn"
                  >
                    <Copy className="h-4 w-4" />
                    Copiar
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setNewKeyRevealed(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Generate new key */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Key className="h-5 w-5" />
                Generar Nueva API Key
              </CardTitle>
              <CardDescription>
                Crea una clave secreta para autenticar las peticiones desde tu web
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <Input
                  data-testid="api-key-name-input"
                  placeholder="Nombre descriptivo (ej. Mi Web Principal, Zapier Producción...)"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleCreateApiKey()}
                  className="flex-1"
                />
                <Button
                  onClick={handleCreateApiKey}
                  disabled={creatingKey || !newKeyName.trim()}
                  className="gap-2 shrink-0"
                  data-testid="generate-api-key-btn"
                >
                  {creatingKey ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4" />
                  )}
                  Generar Key
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Existing keys */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">API Keys Activas</CardTitle>
              <CardDescription>
                Las claves son secretas. Solo se muestra el prefijo para identificación.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {apiKeys.length === 0 ? (
                <div className="text-center py-10 text-slate-600">
                  <Key className="h-10 w-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">No hay API Keys activas. Genera una para empezar.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {apiKeys.map((k) => (
                    <div
                      key={k.id}
                      className="flex items-center justify-between p-4 rounded-xl border border-slate-200 hover:border-slate-300 bg-slate-50 transition-colors"
                      data-testid={`api-key-row-${k.id}`}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                          <Key className="h-4 w-4 text-white" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800 truncate">{k.name}</p>
                          <p className="font-mono text-xs text-slate-500">{k.key_prefix}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 flex-shrink-0 ml-4">
                        <div className="text-right hidden sm:block">
                          <p className="text-xs text-slate-500">Creada</p>
                          <p className="text-xs text-slate-700">
                            {new Date(k.created_at).toLocaleDateString("es-ES")}
                          </p>
                        </div>
                        {k.last_used_at ? (
                          <div className="text-right hidden sm:block">
                            <p className="text-xs text-slate-500">Último uso</p>
                            <p className="text-xs text-emerald-600 font-medium">
                              {new Date(k.last_used_at).toLocaleDateString("es-ES")}
                            </p>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-600 hidden sm:block">Sin uso aún</span>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRevokeKey(k.id, k.name)}
                          className="text-red-500 hover:bg-red-50 hover:text-red-600 gap-1"
                          data-testid={`revoke-key-btn-${k.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="hidden sm:inline">Revocar</span>
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* API Documentation */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <BookOpen className="h-5 w-5" />
                Documentación de la API
              </CardTitle>
              <CardDescription>
                Copia estos ejemplos para integrar tu web con SkiFlow
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Endpoint info */}
              <div className="p-4 rounded-xl bg-slate-900 border border-slate-700">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-emerald-500 text-white font-mono text-xs">POST</Badge>
                    <span className="font-mono text-sm text-slate-200">/api/v1/external-bookings</span>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(`${API_BASE_URL}/api/v1/external-bookings`)}
                    className="text-slate-600 hover:text-white h-7"
                  >
                    <Copy className="h-3.5 w-3.5 mr-1" />
                    Copiar URL
                  </Button>
                </div>
                <div className="font-mono text-xs text-slate-600 break-all">
                  {API_BASE_URL}/api/v1/external-bookings
                </div>
              </div>

              {/* Required Headers */}
              <div>
                <h4 className="font-semibold text-sm text-slate-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  Headers Obligatorios
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <code className="font-mono text-xs bg-slate-200 px-2 py-1 rounded">Authorization</code>
                    <span className="text-sm text-slate-600">Bearer &lt;TU_API_KEY&gt;</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                    <code className="font-mono text-xs bg-slate-200 px-2 py-1 rounded">Content-Type</code>
                    <span className="text-sm text-slate-600">application/json</span>
                  </div>
                </div>
              </div>

              {/* JSON Schema */}
              <div>
                <h4 className="font-semibold text-sm text-slate-900 mb-3 flex items-center gap-2">
                  <Code2 className="h-4 w-4 text-blue-500" />
                  Esquema JSON del Payload
                </h4>
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => copyToClipboard(`{
  "customer_name": "Juan García",
  "customer_email": "juan@email.com",
  "customer_phone": "+34 600 000 000",
  "customer_id_number": "12345678X",
  "start_date": "2026-01-20",
  "end_date": "2026-01-22",
  "items": ["Pack Esquí Adulto", "Casco Infantil"],
  "reference": "#WEB-1234",
  "notes": "Talla de bota 42"
}`)}
                    className="absolute top-2 right-2 text-slate-600 hover:text-white h-7 z-10"
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  <pre className="bg-slate-900 text-emerald-400 rounded-xl p-4 text-xs overflow-x-auto leading-relaxed">
{`{
  "customer_name": "Juan García",       // ✅ Obligatorio
  "customer_email": "juan@email.com",   // Opcional
  "customer_phone": "+34 600 000 000",  // Opcional
  "customer_id_number": "12345678X",    // Opcional (DNI/Pasaporte)
  "start_date": "2026-01-20",           // ✅ Obligatorio (YYYY-MM-DD)
  "end_date": "2026-01-22",             // ✅ Obligatorio (YYYY-MM-DD)
  "items": [                            // ✅ Obligatorio
    "Pack Esquí Adulto",
    "Casco Infantil"
  ],
  "reference": "#WEB-1234",             // Opcional (se genera si vacío)
  "notes": "Talla de bota 42"           // Opcional
}`}</pre>
                </div>
              </div>

              {/* cURL Example */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                    <Code2 className="h-4 w-4 text-orange-500" />
                    Ejemplo cURL (Terminal / Línea de comandos)
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(`curl -X POST "${API_BASE_URL}/api/v1/external-bookings" \\
  -H "Authorization: Bearer sk_live_TU_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_name": "Juan García",
    "customer_email": "juan@email.com",
    "start_date": "2026-01-20",
    "end_date": "2026-01-22",
    "items": ["Pack Esquí Adulto", "Casco Infantil"]
  }'`)}
                    className="gap-1 h-7 text-xs"
                  >
                    <Copy className="h-3 w-3" />
                    Copiar cURL
                  </Button>
                </div>
                <pre className="bg-slate-900 text-amber-300 rounded-xl p-4 text-xs overflow-x-auto leading-relaxed">
{`curl -X POST "${API_BASE_URL}/api/v1/external-bookings" \\
  -H "Authorization: Bearer sk_live_TU_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "customer_name": "Juan García",
    "customer_email": "juan@email.com",
    "start_date": "2026-01-20",
    "end_date": "2026-01-22",
    "items": ["Pack Esquí Adulto", "Casco Infantil"]
  }'`}</pre>
              </div>

              {/* JavaScript Fetch Example */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm text-slate-900 flex items-center gap-2">
                    <Code2 className="h-4 w-4 text-yellow-500" />
                    Ejemplo JavaScript (Fetch API)
                  </h4>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyToClipboard(`const response = await fetch("${API_BASE_URL}/api/v1/external-bookings", {
  method: "POST",
  headers: {
    "Authorization": "Bearer sk_live_TU_API_KEY",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    customer_name: "Juan García",
    customer_email: "juan@email.com",
    start_date: "2026-01-20",
    end_date: "2026-01-22",
    items: ["Pack Esquí Adulto", "Casco Infantil"]
  })
});

const data = await response.json();
console.log(data); // { ok: true, booking_id: "...", reference: "..." }`)}
                    className="gap-1 h-7 text-xs"
                  >
                    <Copy className="h-3 w-3" />
                    Copiar JS
                  </Button>
                </div>
                <pre className="bg-slate-900 text-sky-300 rounded-xl p-4 text-xs overflow-x-auto leading-relaxed">
{`const response = await fetch("${API_BASE_URL}/api/v1/external-bookings", {
  method: "POST",
  headers: {
    "Authorization": "Bearer sk_live_TU_API_KEY",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({
    customer_name: "Juan García",
    customer_email: "juan@email.com",
    start_date: "2026-01-20",
    end_date: "2026-01-22",
    items: ["Pack Esquí Adulto", "Casco Infantil"]
  })
});

const data = await response.json();
console.log(data); // { ok: true, booking_id: "...", reference: "..." }`}</pre>
              </div>

              {/* No-Code Section */}
              <div className="p-5 rounded-xl bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
                    <Zap className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-slate-900 mb-1">Integración Sin Código (Zapier / Make)</h4>
                    <p className="text-sm text-slate-600 mb-3">
                      Configura un <strong>"HTTP Request"</strong> o <strong>"Webhook"</strong> en tu automatización:
                    </p>
                    <ul className="text-sm text-slate-700 space-y-1.5">
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                        <span><strong>URL:</strong> <code className="bg-white px-1 rounded text-xs">{API_BASE_URL}/api/v1/external-bookings</code></span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                        <span><strong>Método:</strong> POST</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                        <span><strong>Header Authorization:</strong> Bearer sk_live_TU_KEY</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                        <span><strong>Body:</strong> JSON con los campos del esquema</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Response Example */}
              <div>
                <h4 className="font-semibold text-sm text-slate-900 mb-3 flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  Respuesta Exitosa (HTTP 200)
                </h4>
                <pre className="bg-slate-900 text-emerald-400 rounded-xl p-4 text-xs overflow-x-auto">
{`{
  "ok": true,
  "booking_id": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
  "reference": "#WEB-1234",
  "status": "online_pending",
  "message": "Booking received. Pending in-store fulfillment."
}`}</pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* WhatsApp Integration */}
        <TabsContent value="whatsapp">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                    <MessageSquare className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle>WhatsApp Business</CardTitle>
                    <CardDescription>Envía avisos automáticos a tus clientes</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={whatsappConfig.enabled ? "default" : "secondary"}>
                    {whatsappConfig.enabled ? (
                      <><Check className="h-3 w-3 mr-1" /> Conectado</>
                    ) : (
                      <><X className="h-3 w-3 mr-1" /> No conectado</>
                    )}
                  </Badge>
                  <Switch
                    checked={whatsappConfig.enabled}
                    onCheckedChange={(v) => setWhatsappConfig({ ...whatsappConfig, enabled: v })}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Número de empresa</Label>
                  <Input
                    value={whatsappConfig.phone_number}
                    onChange={(e) => setWhatsappConfig({ ...whatsappConfig, phone_number: e.target.value })}
                    placeholder="+34 600 000 000"
                    className="h-11 mt-1"
                  />
                </div>
                <div>
                  <Label>API Key</Label>
                  <Input
                    type="password"
                    value={whatsappConfig.api_key}
                    onChange={(e) => setWhatsappConfig({ ...whatsappConfig, api_key: e.target.value })}
                    placeholder="••••••••••••••••"
                    className="h-11 mt-1"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Opciones de envío automático</Label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <span className="text-sm">Recordatorios antes del inicio</span>
                    <Switch
                      checked={whatsappConfig.auto_reminders}
                      onCheckedChange={(v) => setWhatsappConfig({ ...whatsappConfig, auto_reminders: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <span className="text-sm">Confirmaciones de reserva</span>
                    <Switch
                      checked={whatsappConfig.send_confirmations}
                      onCheckedChange={(v) => setWhatsappConfig({ ...whatsappConfig, send_confirmations: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <span className="text-sm">Avisos de devolución pendiente</span>
                    <Switch
                      checked={whatsappConfig.send_overdue_alerts}
                      onCheckedChange={(v) => setWhatsappConfig({ ...whatsappConfig, send_overdue_alerts: v })}
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium mb-3 block">Plantilla de recordatorio</Label>
                <Textarea
                  value={whatsappConfig.templates?.reminder_start || ""}
                  onChange={(e) => setWhatsappConfig({ 
                    ...whatsappConfig, 
                    templates: { ...whatsappConfig.templates, reminder_start: e.target.value }
                  })}
                  className="font-mono text-sm"
                  rows={3}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Variables: {'{nombre}'}, {'{fecha_inicio}'}, {'{fecha_fin}'}, {'{articulos}'}, {'{total}'}
                </p>
              </div>

              <div className="flex gap-2 pt-4 border-t border-slate-200">
                <Button variant="outline" onClick={() => testConnection('WhatsApp')}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Probar conexión
                </Button>
                <Button onClick={saveWhatsappConfig} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Guardar configuración
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TPV Integration */}
        <TabsContent value="tpv">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle>TPV / Datáfono</CardTitle>
                    <CardDescription>Sincroniza pagos con tarjeta automáticamente</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={tpvConfig.enabled ? "default" : "secondary"}>
                    {tpvConfig.enabled ? (
                      <><Check className="h-3 w-3 mr-1" /> Conectado</>
                    ) : (
                      <><X className="h-3 w-3 mr-1" /> No conectado</>
                    )}
                  </Badge>
                  <Switch
                    checked={tpvConfig.enabled}
                    onCheckedChange={(v) => setTpvConfig({ ...tpvConfig, enabled: v })}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Banco</Label>
                  <Input
                    value={tpvConfig.bank}
                    onChange={(e) => setTpvConfig({ ...tpvConfig, bank: e.target.value })}
                    placeholder="Ej: CaixaBank, Santander..."
                    className="h-11 mt-1"
                  />
                </div>
                <div>
                  <Label>Modelo TPV</Label>
                  <Input
                    value={tpvConfig.terminal_model}
                    onChange={(e) => setTpvConfig({ ...tpvConfig, terminal_model: e.target.value })}
                    placeholder="Ej: Ingenico Move 5000"
                    className="h-11 mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Número de terminal</Label>
                  <Input
                    value={tpvConfig.terminal_number}
                    onChange={(e) => setTpvConfig({ ...tpvConfig, terminal_number: e.target.value })}
                    placeholder="00000000"
                    className="h-11 mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label>API Key / Token</Label>
                  <Input
                    type="password"
                    value={tpvConfig.api_key}
                    onChange={(e) => setTpvConfig({ ...tpvConfig, api_key: e.target.value })}
                    placeholder="••••••••••••••••"
                    className="h-11 mt-1"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Opciones</Label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <span className="text-sm">Sincronización automática de pagos</span>
                    <Switch
                      checked={tpvConfig.auto_sync}
                      onCheckedChange={(v) => setTpvConfig({ ...tpvConfig, auto_sync: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <span className="text-sm">Guardar copias digitales de tickets</span>
                    <Switch
                      checked={tpvConfig.save_receipts}
                      onCheckedChange={(v) => setTpvConfig({ ...tpvConfig, save_receipts: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <span className="text-sm">Reimprimir desde el sistema</span>
                    <Switch
                      checked={tpvConfig.reprint_enabled}
                      onCheckedChange={(v) => setTpvConfig({ ...tpvConfig, reprint_enabled: v })}
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-slate-200">
                <Button variant="outline" onClick={() => testConnection('TPV')}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Probar conexión
                </Button>
                <Button onClick={saveTpvConfig} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Guardar configuración
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VeriFactu Integration */}
        <TabsContent value="verifactu">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-indigo-600" />
                  </div>
                  <div>
                    <CardTitle>VeriFactu (AEAT)</CardTitle>
                    <CardDescription>Sistema de facturación electrónica español</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={verifactuConfig.enabled ? "default" : "secondary"}>
                    {verifactuConfig.enabled ? (
                      <><Check className="h-3 w-3 mr-1" /> Activo</>
                    ) : (
                      <><X className="h-3 w-3 mr-1" /> Inactivo</>
                    )}
                  </Badge>
                  <Switch
                    checked={verifactuConfig.enabled}
                    onCheckedChange={(v) => setVerifactuConfig({ ...verifactuConfig, enabled: v })}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Nota:</strong> VeriFactu es obligatorio para ciertas empresas según normativa AEAT. 
                  Consulta con tu asesor fiscal para determinar si aplica a tu negocio.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>NIF Emisor *</Label>
                  <Input
                    value={verifactuConfig.nif_emisor}
                    onChange={(e) => setVerifactuConfig({ ...verifactuConfig, nif_emisor: e.target.value })}
                    placeholder="A12345678"
                    className="h-11 mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label>Nombre / Razón Social *</Label>
                  <Input
                    value={verifactuConfig.nombre_emisor}
                    onChange={(e) => setVerifactuConfig({ ...verifactuConfig, nombre_emisor: e.target.value })}
                    placeholder="Tu empresa S.L."
                    className="h-11 mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>ID Software</Label>
                  <Input
                    value={verifactuConfig.software_id}
                    onChange={(e) => setVerifactuConfig({ ...verifactuConfig, software_id: e.target.value })}
                    placeholder="SW-XXXXX-XXXX"
                    className="h-11 mt-1 font-mono"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    ID proporcionado por AEAT tras el registro del software
                  </p>
                </div>
                <div>
                  <Label>Versión Software</Label>
                  <Input
                    value={verifactuConfig.software_version}
                    onChange={(e) => setVerifactuConfig({ ...verifactuConfig, software_version: e.target.value })}
                    placeholder="1.0"
                    className="h-11 mt-1"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Opciones</Label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div>
                      <span className="text-sm font-medium block">Modo pruebas</span>
                      <span className="text-xs text-slate-500">Usa el entorno de pre-producción de AEAT</span>
                    </div>
                    <Switch
                      checked={verifactuConfig.test_mode}
                      onCheckedChange={(v) => setVerifactuConfig({ ...verifactuConfig, test_mode: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div>
                      <span className="text-sm font-medium block">Envío automático</span>
                      <span className="text-xs text-slate-500">Envía facturas a AEAT al completar alquiler</span>
                    </div>
                    <Switch
                      checked={verifactuConfig.auto_send}
                      onCheckedChange={(v) => setVerifactuConfig({ ...verifactuConfig, auto_send: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div>
                      <span className="text-sm font-medium block">Guardar archivos XML</span>
                      <span className="text-xs text-slate-500">Almacena copias locales de facturas firmadas</span>
                    </div>
                    <Switch
                      checked={verifactuConfig.save_xml}
                      onCheckedChange={(v) => setVerifactuConfig({ ...verifactuConfig, save_xml: v })}
                    />
                  </div>
                </div>
              </div>

              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-200">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">Estado de la integración</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ Estructura de configuración preparada</li>
                  <li>✓ UI de configuración completa</li>
                  <li className="text-blue-600">⏳ API de firma digital - Pendiente</li>
                  <li className="text-blue-600">⏳ Conexión AEAT - Pendiente</li>
                  <li className="text-blue-600">⏳ Generación de XML - Pendiente</li>
                </ul>
                <p className="text-xs text-blue-700 mt-3">
                  La implementación completa requiere certificados digitales y homologación con AEAT
                </p>
              </div>

              <div className="flex gap-2 pt-4 border-t border-slate-200">
                <Button variant="outline" onClick={() => testConnection('VeriFactu')} disabled>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Probar conexión
                </Button>
                <Button onClick={saveVerifactuConfig} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Guardar configuración
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Integration */}
        <TabsContent value="email">
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-rose-100 flex items-center justify-center">
                    <Mail className="h-5 w-5 text-rose-600" />
                  </div>
                  <div>
                    <CardTitle>Servidor de Email (SMTP)</CardTitle>
                    <CardDescription>Envía confirmaciones y facturas por correo</CardDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={emailConfig.enabled ? "default" : "secondary"}>
                    {emailConfig.enabled ? (
                      <><Check className="h-3 w-3 mr-1" /> Conectado</>
                    ) : (
                      <><X className="h-3 w-3 mr-1" /> No conectado</>
                    )}
                  </Badge>
                  <Switch
                    checked={emailConfig.enabled}
                    onCheckedChange={(v) => setEmailConfig({ ...emailConfig, enabled: v })}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-200">
                <p className="text-sm text-blue-800 mb-2">
                  <strong>Proveedores recomendados:</strong>
                </p>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• <strong>Gmail:</strong> smtp.gmail.com (Puerto 587) - Usar contraseña de aplicación</li>
                  <li>• <strong>Outlook:</strong> smtp.office365.com (Puerto 587)</li>
                  <li>• <strong>SendGrid:</strong> smtp.sendgrid.net (Puerto 587)</li>
                  <li>• <strong>Mailgun:</strong> smtp.mailgun.org (Puerto 587)</li>
                </ul>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Servidor SMTP *</Label>
                  <Input
                    value={emailConfig.smtp_server}
                    onChange={(e) => setEmailConfig({ ...emailConfig, smtp_server: e.target.value })}
                    placeholder="smtp.gmail.com"
                    className="h-11 mt-1"
                  />
                </div>
                <div>
                  <Label>Puerto *</Label>
                  <Input
                    type="number"
                    value={emailConfig.smtp_port}
                    onChange={(e) => setEmailConfig({ ...emailConfig, smtp_port: e.target.value })}
                    placeholder="587"
                    className="h-11 mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Email remitente *</Label>
                  <Input
                    type="email"
                    value={emailConfig.email_from}
                    onChange={(e) => setEmailConfig({ ...emailConfig, email_from: e.target.value })}
                    placeholder="tutienda@ejemplo.com"
                    className="h-11 mt-1"
                  />
                </div>
                <div>
                  <Label>Contraseña / API Key *</Label>
                  <Input
                    type="password"
                    value={emailConfig.email_password}
                    onChange={(e) => setEmailConfig({ ...emailConfig, email_password: e.target.value })}
                    placeholder="••••••••••••••••"
                    className="h-11 mt-1"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Opciones de envío automático</Label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div>
                      <span className="text-sm font-medium block">Usar TLS/SSL</span>
                      <span className="text-xs text-slate-500">Conexión segura (recomendado)</span>
                    </div>
                    <Switch
                      checked={emailConfig.use_tls}
                      onCheckedChange={(v) => setEmailConfig({ ...emailConfig, use_tls: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div>
                      <span className="text-sm font-medium block">Confirmaciones de reserva</span>
                      <span className="text-xs text-slate-500">Enviar email al completar un alquiler</span>
                    </div>
                    <Switch
                      checked={emailConfig.send_confirmations}
                      onCheckedChange={(v) => setEmailConfig({ ...emailConfig, send_confirmations: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div>
                      <span className="text-sm font-medium block">Recordatorios de devolución</span>
                      <span className="text-xs text-slate-500">Email 1 día antes del fin del alquiler</span>
                    </div>
                    <Switch
                      checked={emailConfig.send_reminders}
                      onCheckedChange={(v) => setEmailConfig({ ...emailConfig, send_reminders: v })}
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div>
                      <span className="text-sm font-medium block">Enviar facturas</span>
                      <span className="text-xs text-slate-500">PDF adjunto con resumen del alquiler</span>
                    </div>
                    <Switch
                      checked={emailConfig.send_invoices}
                      onCheckedChange={(v) => setEmailConfig({ ...emailConfig, send_invoices: v })}
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>URL del logo de la empresa</Label>
                <Input
                  value={emailConfig.company_logo_url}
                  onChange={(e) => setEmailConfig({ ...emailConfig, company_logo_url: e.target.value })}
                  placeholder="https://tutienda.com/logo.png"
                  className="h-11 mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">Se incluirá en la cabecera de los emails</p>
              </div>

              <div>
                <Label>Firma de email</Label>
                <Textarea
                  value={emailConfig.email_signature}
                  onChange={(e) => setEmailConfig({ ...emailConfig, email_signature: e.target.value })}
                  placeholder="Saludos,&#10;Tu Tienda de Esquí&#10;Teléfono: +34 XXX XXX XXX"
                  className="mt-1"
                  rows={3}
                />
              </div>

              <div className="flex gap-2 pt-4 border-t border-slate-200">
                <Button variant="outline" onClick={() => testConnection('Email')}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Enviar email de prueba
                </Button>
                <Button onClick={saveEmailConfig} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Guardar configuración
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
